/**
 * TweeFactorPage — Admin/manager kunnen hier 2FA enrollen of disablen.
 *
 * Flow:
 *   - Niet enrolled: toon "Enroll" knop → GET /auth/2fa/enroll-start → toon QR + secret →
 *     gebruiker scant met Google Authenticator / Authy → vul 6-cijfer code in → POST
 *     /auth/2fa/enroll-confirm → backup codes worden eenmalig getoond
 *   - Wel enrolled: toon "2FA actief" + Disable knop (vereist password + TOTP code)
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import {
  Loader2, ShieldCheck, ShieldOff, QrCode, AlertCircle, Copy, CheckCircle, Download,
} from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

export default function TweeFactorPage() {
  const { getToken } = useAuth();
  const token = getToken();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrollStep, setEnrollStep] = useState(null); // null | 'qr' | 'backup'
  const [enrollData, setEnrollData] = useState(null);
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [backupCodes, setBackupCodes] = useState([]);
  // Disable form
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');

  const loadStatus = async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${BACKEND}/api/auth/2fa/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatus(res.data);
    } catch (e) {
      toast.error('Kon 2FA status niet laden');
    }
    setLoading(false);
  };

  useEffect(() => { loadStatus(); /* eslint-disable-next-line */ }, [token]);

  const startEnroll = async () => {
    setSubmitting(true);
    try {
      const res = await axios.get(`${BACKEND}/api/auth/2fa/enroll-start`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEnrollData(res.data);
      setEnrollStep('qr');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Enrollment mislukt');
    }
    setSubmitting(false);
  };

  const confirmEnroll = async () => {
    if (!code || code.length < 6) {
      toast.error('Voer de 6-cijferige code in uit uw authenticator app');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${BACKEND}/api/auth/2fa/enroll-confirm`,
        { code: code.replace(/\s/g, '') },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setBackupCodes(res.data.backup_codes || []);
      setEnrollStep('backup');
      setCode('');
      toast.success('2FA geactiveerd');
      await loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Code onjuist');
    }
    setSubmitting(false);
  };

  const finishEnroll = () => {
    setEnrollStep(null);
    setEnrollData(null);
    setBackupCodes([]);
  };

  const disable = async () => {
    if (!disablePassword || !disableCode) {
      toast.error('Vul beide velden in');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${BACKEND}/api/auth/2fa/disable`,
        { password: disablePassword, code: disableCode.replace(/\s/g, '') },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('2FA uitgeschakeld');
      setDisablePassword('');
      setDisableCode('');
      await loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Uitschakelen mislukt');
    }
    setSubmitting(false);
  };

  const downloadBackup = () => {
    const text = `IMPERIA AI — 2FA Backup Codes\n\nBewaar deze codes op een veilige plek. Elke code is 1x bruikbaar.\n\n${backupCodes.join('\n')}\n`;
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'imperia-ai-2fa-backup-codes.txt';
    a.click();
  };

  if (loading) return <div style={{ padding: 40, color: '#94a3b8' }}><Loader2 size={20} className="animate-spin" /> Laden…</div>;

  if (!status?.eligible) {
    return (
      <div style={{ padding: 24, color: 'white', maxWidth: 640 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, margin: 0, color: 'white' }}>Twee-staps verificatie</h1>
        <div style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 10, padding: 16, marginTop: 16, color: '#fde68a' }}>
          <AlertCircle size={18} style={{ display: 'inline', marginRight: 8, verticalAlign: 'middle' }} />
          2FA is alleen beschikbaar voor admin/manager/projectmanager rollen.
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 24, color: 'white', maxWidth: 720 }} data-testid="2fa-page">
      <h1 style={{ fontFamily: "'Sora', sans-serif", fontSize: 28, fontWeight: 800, margin: 0, color: 'white' }}>Twee-staps verificatie (2FA)</h1>
      <p style={{ color: '#cbd5e1', marginTop: 4, marginBottom: 24, fontSize: 14, lineHeight: 1.6 }}>
        Extra beveiligingslaag voor uw account. Vereist een authenticator-app (Google Authenticator, Authy, 1Password, Bitwarden).
      </p>

      {/* ENROLL STEP 1: QR + Code input */}
      {enrollStep === 'qr' && enrollData && (
        <div style={{ background: 'rgba(15,23,42,0.6)', border: '1px solid #1e293b', borderRadius: 14, padding: 24 }}>
          <p style={{ color: '#D4A017', fontWeight: 700, margin: 0, marginBottom: 4 }}>Stap 1 van 2 — Scan QR code</p>
          <p style={{ color: '#94a3b8', fontSize: 13, margin: '0 0 14px' }}>Open uw authenticator-app en scan de onderstaande QR-code.</p>

          <div style={{ background: 'white', padding: 14, borderRadius: 10, display: 'inline-block', marginBottom: 14 }}>
            <img src={enrollData.qr_data_url} alt="2FA QR code" data-testid="2fa-qr-code" style={{ display: 'block', width: 200, height: 200 }} />
          </div>

          <details style={{ marginBottom: 14, fontSize: 13, color: '#94a3b8' }}>
            <summary style={{ cursor: 'pointer', color: '#cbd5e1' }}>Of voer de geheime sleutel handmatig in</summary>
            <code style={{ display: 'block', background: '#0f172a', padding: 10, borderRadius: 6, marginTop: 6, fontFamily: 'monospace', fontSize: 12, color: '#a7f3d0', wordBreak: 'break-all' }}>
              {enrollData.secret}
            </code>
          </details>

          <label style={{ display: 'block', marginTop: 14 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#cbd5e1', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>Code uit uw authenticator</span>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              data-testid="2fa-enroll-code"
              placeholder="123456"
              maxLength={8}
              style={{ background: '#0f172a', color: 'white', border: '1px solid #1e293b', padding: '12px 14px', borderRadius: 8, fontSize: 18, width: 180, fontFamily: 'monospace', letterSpacing: '0.2em', textAlign: 'center' }}
            />
          </label>

          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <button onClick={confirmEnroll} disabled={submitting || code.length < 6} data-testid="2fa-confirm" style={{ background: '#D4A017', color: '#0f172a', fontWeight: 800, padding: '12px 22px', borderRadius: 10, border: 'none', cursor: submitting || code.length < 6 ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />} Verifieer & activeer
            </button>
            <button onClick={finishEnroll} disabled={submitting} style={{ background: 'transparent', color: '#94a3b8', padding: '12px 18px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer' }}>
              Annuleer
            </button>
          </div>
        </div>
      )}

      {/* ENROLL STEP 2: backup codes */}
      {enrollStep === 'backup' && (
        <div style={{ background: 'rgba(15,23,42,0.6)', border: '1px solid #10b981', borderRadius: 14, padding: 24 }}>
          <p style={{ color: '#10b981', fontWeight: 700, margin: 0, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
            <ShieldCheck size={18} /> 2FA actief — bewaar uw backup codes!
          </p>
          <p style={{ color: '#94a3b8', fontSize: 13, margin: '4px 0 14px', lineHeight: 1.5 }}>
            Mocht u uw telefoon verliezen kunt u inloggen met één van deze 10 codes (elk eenmalig bruikbaar). Bewaar ze in een wachtwoordmanager of print ze uit.
          </p>

          <div style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 10, padding: 16, fontFamily: 'monospace', fontSize: 14, marginBottom: 14 }} data-testid="2fa-backup-codes">
            {backupCodes.map((c, i) => (
              <div key={c} style={{ padding: '4px 0', color: '#a7f3d0', display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#64748b' }}>{(i + 1).toString().padStart(2, '0')}.</span>
                <span>{c}</span>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
            <button onClick={downloadBackup} data-testid="2fa-download-backup" style={{ background: 'rgba(255,255,255,0.05)', color: 'white', padding: '10px 16px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Download size={14} /> Download als .txt
            </button>
            <button onClick={() => { navigator.clipboard.writeText(backupCodes.join('\n')); toast.success('Gekopieerd'); }} style={{ background: 'rgba(255,255,255,0.05)', color: 'white', padding: '10px 16px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Copy size={14} /> Kopieer
            </button>
          </div>

          <button onClick={finishEnroll} data-testid="2fa-finish" style={{ background: '#D4A017', color: '#0f172a', fontWeight: 800, padding: '12px 22px', borderRadius: 10, border: 'none', cursor: 'pointer' }}>
            Ik heb de codes opgeslagen — sluit
          </button>
        </div>
      )}

      {/* DEFAULT: Status overview */}
      {!enrollStep && (
        <>
          {status?.two_fa_enabled ? (
            <div style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid #10b981', borderRadius: 14, padding: 20, marginBottom: 16 }}>
              <p style={{ color: '#10b981', fontWeight: 800, margin: 0, fontSize: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                <ShieldCheck size={20} /> 2FA actief
              </p>
              <p style={{ color: '#cbd5e1', fontSize: 13, marginTop: 6 }}>
                Geactiveerd op {(status.enrolled_at || '').slice(0, 10)} · {status.backup_codes_remaining} backup codes resterend
              </p>
              {status.backup_codes_remaining < 3 && (
                <p style={{ color: '#fde68a', fontSize: 12, marginTop: 8 }}>
                  ⚠ U heeft nog maar {status.backup_codes_remaining} backup codes. Schakel 2FA uit en opnieuw aan om nieuwe te genereren.
                </p>
              )}
            </div>
          ) : (
            <div style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 14, padding: 20, marginBottom: 16, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <ShieldOff size={22} color="#f59e0b" />
              <div>
                <p style={{ color: '#fde68a', fontWeight: 800, margin: 0 }}>2FA staat uit</p>
                <p style={{ color: '#fde68a99', fontSize: 13, marginTop: 4 }}>Voor admin accounts wordt 2FA sterk aangeraden. Voeg een tweede beveiligingslaag toe.</p>
              </div>
            </div>
          )}

          {!status?.two_fa_enabled && (
            <button onClick={startEnroll} disabled={submitting} data-testid="2fa-enroll-start" style={{ background: '#D4A017', color: '#0f172a', fontWeight: 800, padding: '14px 28px', borderRadius: 10, border: 'none', cursor: submitting ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <QrCode size={16} />} 2FA inschakelen
            </button>
          )}

          {/* Disable form (only if enabled) */}
          {status?.two_fa_enabled && (
            <div style={{ background: 'rgba(15,23,42,0.6)', border: '1px solid #1e293b', borderRadius: 14, padding: 20, marginTop: 16 }}>
              <p style={{ fontSize: 14, fontWeight: 700, color: '#fca5a5', margin: 0 }}>Schakel 2FA uit</p>
              <p style={{ color: '#94a3b8', fontSize: 12, marginTop: 4, marginBottom: 12 }}>Vereist uw huidige wachtwoord + een 2FA code (TOTP of backup).</p>
              <input type="password" placeholder="Wachtwoord" value={disablePassword} onChange={(e) => setDisablePassword(e.target.value)} data-testid="2fa-disable-password" style={inputStyle} />
              <input type="text" placeholder="6-cijfer code of backup code" value={disableCode} onChange={(e) => setDisableCode(e.target.value)} data-testid="2fa-disable-code" style={{ ...inputStyle, fontFamily: 'monospace' }} />
              <button onClick={disable} disabled={submitting || !disablePassword || !disableCode} data-testid="2fa-disable-submit" style={{ background: '#ef4444', color: 'white', fontWeight: 700, padding: '10px 20px', borderRadius: 8, border: 'none', cursor: submitting || !disablePassword || !disableCode ? 'not-allowed' : 'pointer' }}>
                Schakel 2FA uit
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const inputStyle = {
  width: '100%',
  background: '#0f172a',
  color: 'white',
  border: '1px solid #1e293b',
  padding: '10px 12px',
  borderRadius: 8,
  fontSize: 14,
  marginBottom: 10,
};
